<template>
  <div class="b-card-container flex-2">
    <div class="bc-header-container">
      <div class="bc-header">
        {{ hvacDetail.systemType }} System: {{ hvacIndex + 1 }} of
        {{ filteredHvac.length }}
      </div>
      <div class="grid-column ai-c gap-16">
        <button
          type="button"
          :disabled="hvacIndex === 0"
          :class="['nav-btn', hvacIndex === 0 ? 'disabled' : '']"
          @click="prevHvac"
        >
          <img
            src="@shared/assets/img/general/chevron-left.svg"
            class="img-size-20"
            alt="prev"
          />
        </button>
        <button
          type="button"
          :disabled="hvacIndex === filteredHvac.length - 1"
          :class="['nav-btn', hvacIndex === filteredHvac.length - 1 ? 'disabled' : '']"
          @click="nextHvac"
        >
          <img
            src="@shared/assets/img/general/chevron-right.svg"
            class="img-size-20"
            alt="next"
          />
        </button>
      </div>
      <button v-if="isSmacAdmin" type="button" class="edit-btn" @click="editHvac">
        Edit
      </button>
    </div>
    <div class="hvac-detail-container">
      <div class="grid-column ai-c jc-b">
        <div class="sac__font-14 n70">UID / System</div>
        <div class="sac__font-14">{{ hvacDetail.uid }} / {{ hvacDetail.systemName }}</div>
      </div>
      <div class="grid-column ai-c jc-b">
        <div class="sac__font-14 n70">Last service date</div>
        <div class="row">
          <div class="sac__font-14">
            {{ dateWithoutHour(hvacDetail.lastServiceDate, dateAndYearFormat) }}
          </div>
          <button
            v-if="adminRole !== 'viewer'"
            class="sac__font-14 semi-bold f-primary"
            @click="setProfileEdit(hvacDetail)"
          >
            Edit
          </button>
        </div>
      </div>
      <div v-if="showCondenserInfo" class="condenser-container">
        <div class="grid-row gap-16">
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Manufacturer / Model</div>
            <div class="sac__font-14">
              {{ hvacDetail.manufacturer || "--" }} / {{ hvacDetail.model || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">System Age - Mfg Date</div>
            <div class="sac__font-14">
              {{ systemAge(hvacDetail.systemAge) }} - {{ hvacDetail.mfgDate || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Serial Number</div>
            <div class="sac__font-14">
              {{ hvacDetail.serialNumber || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Type / Speed</div>
            <div class="sac__font-14">
              {{ hvacDetail.type || "--" }} / {{ hvacDetail.speed || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Tonnage</div>
            <div class="sac__font-14">
              {{ hvacDetail.tonnage || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Condenser Amp</div>
            <div class="sac__font-14">
              {{ hvacDetail.condenserAmp || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">SEER / EER</div>
            <div class="sac__font-14">
              {{ hvacDetail.seer || "--" }} / {{ hvacDetail.eer || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Refrigerant</div>
            <div class="sac__font-14">
              {{ hvacDetail.refrigerant || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Factory Charge</div>
            <div class="sac__font-14">
              {{ hvacDetail.factoryCharge || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Warranty Expire</div>
            <div class="sac__font-14">
              {{ dateV2(hvacDetail.warranty, dateFormat) }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Breakdown Risk</div>
            <div class="grid-column ai-c gap-5">
              <span v-if="!hvacDetail.profileCompletedDate" class="sac__font-14 n70 med">
                Pending
              </span>
              <button
                v-else
                type="button"
                :class="[`sac__font-14  ${textColor} med underline`]"
                @click="toggleActionModal(true, 'breakdown-risk', hvacDetail)"
              >
                {{ hvacDetail.breakdownRiskScore }} / {{ hvacDetail.breakdownRiskRating }}
              </button>
            </div>
          </div>
          <div v-if="performanceScoreEnabled" class="grid-column ai-c jc-b">
            <div class="grid-column ai-c gap-5">
              <span class="sac__font-14 n70">Performance Score</span>
              <button
                type="button"
                class="ps-info-btn"
                @click="toggleActionModal(true, 'performance-score-info', hvacDetail)"
              >
                <img
                  src="@shared/assets/img/general/info-grey.svg"
                  alt="info"
                  class="ps-info-icon"
                />
              </button>
            </div>
            <div class="grid-column ai-c gap-5">
              <span v-if="loadingPerformanceScores" class="sac__font-14 n70 med">
                Loading...
              </span>
              <span v-else-if="!latestScore" class="sac__font-14 n70 med">--</span>
              <button
                v-else
                type="button"
                class="sac__font-14 med underline"
                @click="toggleActionModal(true, 'performance-score', hvacDetail)"
              >
                {{ latestScoreLabel }} Performance on
                {{ formatScoreDate(latestScore.timestamp) }}
                <span class="semi-bold">{{ latestScore.grade }}</span>
              </button>
            </div>
          </div>
        </div>
        <div>
          <HVACImage
            :imageUrl="hvacDetail.imageUrl"
            :cHvac="hvacDetail"
            :uploadOption="true"
            :nameplateOption="
              !hvacDetail.profileCompletedDate &&
              (hvacDetail.unreadableImage || hvacDetail.unusableImage)
            "
            :change="handleCondenserUpload"
          />
        </div>
      </div>
      <div v-if="showFurnaceInfo" class="furnace-container">
        <div class="grid-row gap-16">
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Furnace Manufacturer / Model</div>
            <div class="sac__font-14">
              {{ hvacDetail.furnaceManufacturer || "--" }} /
              {{ hvacDetail.furnaceModel || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Furnace Mfg Date</div>
            <div class="sac__font-14">
              {{ hvacDetail.furnaceMfgDate || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Serial Number</div>
            <div class="sac__font-14">
              {{ hvacDetail.furnaceSerialNumber || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Heat Type / Speed</div>
            <div class="sac__font-14">
              {{ hvacDetail.furnaceHeatType || "--" }} /
              {{ hvacDetail.furnaceSpeed || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Heat Capacity</div>
            <div class="sac__font-14">
              {{ hvacDetail.furnaceHeatCapacity || "--" }}
            </div>
          </div>
          <div class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Warranty Expire</div>
            <div class="sac__font-14">
              {{ dateV2(hvacDetail.furnaceWarranty, dateFormat) }}
            </div>
          </div>
          <div v-if="!showCondenserInfo" class="grid-column ai-c jc-b">
            <div class="sac__font-14 n70">Breakdown Risk</div>
            <div class="grid-column ai-c gap-5">
              <span v-if="!hvacDetail.profileCompletedDate" class="sac__font-14 n70 med">
                Pending
              </span>
              <button
                v-else
                type="button"
                :class="[`sac__font-14  ${textColor} med underline`]"
                @click="toggleActionModal(true, 'breakdown-risk', hvacDetail)"
              >
                {{ hvacDetail.breakdownRiskScore }} / {{ hvacDetail.breakdownRiskRating }}
              </button>
            </div>
          </div>
          <div
            v-if="!showCondenserInfo && performanceScoreEnabled"
            class="grid-column ai-c jc-b"
          >
            <div class="grid-column ai-c gap-5">
              <span class="sac__font-14 n70">Performance Score</span>
              <button
                type="button"
                class="ps-info-btn"
                @click="toggleActionModal(true, 'performance-score-info', hvacDetail)"
              >
                <img
                  src="@shared/assets/img/general/info-grey.svg"
                  alt="info"
                  class="ps-info-icon"
                />
              </button>
            </div>
            <div class="grid-column ai-c gap-5">
              <span v-if="loadingPerformanceScores" class="sac__font-14 n70 med">
                Loading...
              </span>
              <span v-else-if="!latestScore" class="sac__font-14 n70 med">--</span>
              <button
                v-else
                type="button"
                class="sac__font-14 med underline"
                @click="toggleActionModal(true, 'performance-score', hvacDetail)"
              >
                {{ latestScoreLabel }} Performance on
                {{ formatScoreDate(latestScore.timestamp) }}
                <span class="semi-bold">{{ latestScore.grade }}</span>
              </button>
            </div>
          </div>
        </div>
        <HVACImage
          :imageUrl="hvacDetail.furnaceImageUrl"
          :cHvac="hvacDetail"
          :uploadOption="true"
          :nameplateOption="
            !hvacDetail.profileCompletedDate &&
            (hvacDetail.unreadableImage || hvacDetail.unusableImage)
          "
          :change="handleFurnaceUpload"
        />
      </div>
    </div>
  </div>
</template>

<script>
import customerDetailMixin from "@shared/mixins/customerDetailMixin";
import launchDarklyClient from "@shared/lib/general/launchDarklyClient";

import HVACImage from "@shared/components/customerDetail/HVACImage.vue";
import unitPreferencesMixin from "@shared/mixins/unitPreferencesMixin";

const FLAG_KEY = "prs-performance-score-corporation-ids";

export default {
  name: "HVACDetail",
  components: {
    HVACImage,
  },
  mixins: [customerDetailMixin, unitPreferencesMixin],
  data() {
    return {
      hvacIndex: 0,
      performanceScoreEnabled: false,
    };
  },
  watch: {
    hvacDetail: {
      async handler(val) {
        if (!val || !val.serialNumber) return;
        const corpId = this.customer?.corporationId;
        if (!corpId) return;
        const allowedCorpIds = await launchDarklyClient.getFeatureFlag(FLAG_KEY, []);
        this.performanceScoreEnabled = allowedCorpIds.includes(corpId);
        if (this.performanceScoreEnabled) {
          this.$store.dispatch("getPerformanceScores", val.serialNumber);
        }
      },
      immediate: true,
    },
  },
  computed: {
    hvacDetail() {
      if (!(this.filteredHvac && this.filteredHvac.length)) {
        return {};
      }
      return this.filteredHvac[this.hvacIndex];
    },
    coolingData() {
      return this.performanceScores?.cooling || null;
    },
    heatingData() {
      return this.performanceScores?.heating || null;
    },
    latestCooling() {
      return this.coolingData?.latest || null;
    },
    latestHeating() {
      return this.heatingData?.latest || null;
    },
    latestScore() {
      const cooling = this.latestCooling;
      const heating = this.latestHeating;
      if (!cooling) return heating || null;
      if (!heating) return cooling;
      return new Date(heating.timestamp) > new Date(cooling.timestamp) ? heating : cooling;
    },
    latestScoreLabel() {
      if (!this.latestScore) return "";
      return this.latestScore === this.latestHeating ? "Heating" : "Cooling";
    },
    textColor() {
      const { breakdownRiskRating } = this.hvacDetail;
      if (!breakdownRiskRating) {
        return "";
      }
      if (breakdownRiskRating === "Low") {
        return "f-green";
      }
      if (breakdownRiskRating === "Medium") {
        return "f-yellow";
      }
      if (breakdownRiskRating === "High") {
        return "tomato";
      }
      return "";
    },
    showCondenserInfo() {
      const { systemType } = this.hvacDetail;
      return systemType?.toLowerCase() !== "furnace";
    },
    showFurnaceInfo() {
      const { systemType } = this.hvacDetail;
      if (!systemType) {
        return false;
      }
      return (
        systemType?.toLowerCase() === "furnace" || systemType?.toLowerCase() === "split"
      );
    },
  },
  methods: {
    formatScoreDate(timestamp) {
      if (!timestamp) return "";
      return this.dateV2(timestamp);
    },
    setProfileEdit(profile) {
      this.$store.dispatch("changeCustomerDetailValue", {
        field: "propertyProfile",
        value: { ...profile, corpId: this.customer.corporationId },
      });
      this.toggleActionModal(true, "edit-lastservice-date");
    },
    editHvac() {
      const hvac = this.filteredHvac[this.hvacIndex];
      this.$store.dispatch("changeHvacProfileValues", [
        {
          field: "condenserImage",
          value: {
            unusable: hvac.condenserUnusable,
            duplicate: hvac.condenserDuplicate,
            unusableReason: hvac.condenserUnusableReason,
          },
        },
        {
          field: "furnaceImage",
          value: {
            unusable: hvac.furnaceUnusable,
            duplicate: hvac.furnaceDuplicate,
            unusableReason: hvac.furnaceUnusableReason,
          },
        },
      ]);
      this.toggleHVACProfileActionModal(true, "profile-info", hvac);
    },
    handleFurnaceUpload(show, view, item) {
      this.$store.dispatch("changeCustomerDetailValue", {
        field: "uploadImageType",
        value: "furnace",
      });
      this.toggleActionModal(show, view, item);
    },
    handleCondenserUpload(show, view, item) {
      this.$store.dispatch("changeCustomerDetailValue", {
        field: "uploadImageType",
        value: "nameplate",
      });
      this.toggleActionModal(show, view, item);
    },
    nextHvac() {
      if (this.hvacIndex < this.filteredHvac.length - 1) {
        this.hvacIndex += 1;
      }
    },
    prevHvac() {
      if (this.hvacIndex > 0) {
        this.hvacIndex -= 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.hvac-detail-container {
  display: grid;
  gap: 1rem;
  padding: 1rem;
  .condenser-container {
    display: grid;
    gap: 2rem;
    border-radius: 8px;
    border: 2px solid $NBLUE70;
    padding: 16px 8px;
    align-items: flex-start;
    grid-template-columns: 2fr 1fr;
  }
  .furnace-container {
    display: grid;
    gap: 2rem;
    border-radius: 8px;
    border: 2px solid $TOMATO25;
    padding: 16px 8px;
    align-items: flex-start;
    grid-template-columns: 2fr 1fr;
  }
}
.edit-btn {
  color: $WHITE;
  border: 1px solid $WHITE;
  border-radius: 4px;
  padding: 1px 8px;
  font-size: 16px;
  font-weight: 600;
}
.nav-btn {
  background-color: $N10;
  display: grid;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  height: 30px;
  width: 30px;
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
.ps-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;

  .ps-info-icon {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }

  &:hover .ps-info-icon {
    opacity: 1;
  }
}
</style>
